/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-key */
import { FunctionComponent as F, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { IState } from "../../core/store/store";
import {
  addToProtocolAsync,
  swapIndividualListingIntoProtocolAsync } from "../../core/store/protocol/protocol.thunks";
import MaUTable from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import AddIcon from '@mui/icons-material/Add';
import { LOGIN, NEW } from "../../core/router/paths";
import PriceWithTaxPlusDelivery from "./PriceWithTaxPlusDelivery";
import ListedPrice from "./ListedPrice";
import { deleteListingAsync } from "../../core/store/listings/listings.thunks";
import { useAppDispatch } from "../../core/store/hooks";
import { alphabeticalSort } from "../../utils/string";
import { TListingTableRow } from "@protoplan/types";
import HeadingSort from "../UI/Table/HeadingSort";
import LinkRow from "../UI/Table/LinkRow";
import { SortDirection, SortFn, TableHeading } from "../../core/types/component";
import { handleToggleSortValues } from "../../utils/table";
import TextTooltip from "../UI/Tooltip/TextTooltip";
import InfoIcon from '@mui/icons-material/Info';
import { Stack } from "@mui/material";
import { updateListings } from "../../core/store/listings/listings.slice";
import LinkOpt from "../UI/Icons/LinkOpt";


interface Props
{
  swapDosingId?: number,
  protocolId: number,
  listings: TListingTableRow[];
  bundles?: boolean;
  listingName?: boolean;
}

const ListingsTable: F<Props> = (props: Props) =>
{
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const user = useSelector((state: IState) => state.user);
  const storedProductId = useSelector((state: IState) => state.listings.productId);

  const [vendorSort, setVendorSort] = useState<SortDirection | null>(SortDirection.alphabetical);
  const [priceSort, setPriceSort] = useState<SortDirection | null>(null);
  const [priceWithFeesSort, setPriceWithFeesSort] = useState<SortDirection | null>(null);
  const [nameSort, setNameSort] = useState<SortDirection | null>(null);
  const allSetSorts = [setVendorSort, setPriceSort, setPriceWithFeesSort, setNameSort];

  const handleToggleSort = (
    key: string,
    sortDirection: SortDirection | null,
    setSort: (value: React.SetStateAction<SortDirection | null>) => void,
    sortFn?: SortFn) =>
    handleToggleSortValues<TListingTableRow>({
      values,
      setValues,
      key,
      sortDirection,
      setSortDirection: setSort,
      unsetSortDirections: allSetSorts,
      updateValues: (values: TListingTableRow[]) => dispatch(updateListings(values)),
      sortFn });


  const userListings = !props.listings?.every(listing => !listing.userId);

  const determineHeadings = () =>
  {
    const baseHeadings = [
      {
        Header: 'Vendor',
        accessor: 'vendorName',
        sortDirection: vendorSort,
        setSortDirection: setVendorSort,
        sortFn: (a: string, b: string) => alphabeticalSort(a, b, true) },
      {
        Header: 'Price listed',
        accessor: 'price',
        sortDirection: priceSort,
        setSortDirection: setPriceSort },
      {
        Header: 'Price + tax + delivery (est.)',
        accessor: 'priceWithFees',
        tooltip: "Converted to local currency with latest exchange rate",
        sortDirection: priceWithFeesSort,
        setSortDirection: setPriceWithFeesSort },
      { Header: '', accessor: 'link' } ];

    return (props.swapDosingId ? [{ Header: '', accessor: 'swapIntoProtocol' }] as TableHeading[] : [])
      .concat({ Header: '', accessor: 'addToProtocol' })
      .concat(props.listingName ? [{
        Header: 'Name',
        accessor: 'listingName',
        sortDirection: nameSort,
        setSortDirection: setNameSort,
        sortFn: (a: string, b: string) => alphabeticalSort(a, b, true) }] as TableHeading[] : [])
      .concat(baseHeadings)
      .concat(userListings ? [{ Header: '', accessor: 'remove' }] : []);
  };

  const [headings, setHeadings] = useState(determineHeadings());

  // Values
  const valuesFromListings = props.listings.map(l => ({
    ...l,
    price: l.price + (l.deliveryPerListing || 0),
    discountedPrice: l.discountedPrice + (l.deliveryPerListing || 0),
    priceWithFees: l.priceWithFees + (l.deliveryPrice || 0) * l.exchangeRate }));
  const [values, setValues] = useState(valuesFromListings);

  // Update headings on first render
  useEffect(() =>
  {
    setHeadings(determineHeadings());
  }, []);

  // Update values when listings arrive
  useEffect(() =>
  {
    setValues(valuesFromListings);
  }, [props.listings.length]);

  // Update headings when sort state changed
  useEffect(() =>
  {
    setHeadings(determineHeadings());
  }, [vendorSort, priceSort, priceWithFeesSort]);

  // Listing functionality
  const handleAddToProtocol = (listingId: number) =>
  {
    const listing = props.listings.find(l => l.listingId === listingId);
    if(!listing)
      return;

    dispatch(addToProtocolAsync(
      props.protocolId,
      listingId,
      undefined,
      listing.discounts?.filter(d => d.applied).map(d => d.discountId)));

    navigate("../../../", { relative: 'path' });
  };

  const handleSwapIntoProtocol = (listing: TListingTableRow) =>
  {
    if(!props.swapDosingId)
      return console.log("ListingsTable Error: cannot swap into protocol without dosingId");

    dispatch(swapIndividualListingIntoProtocolAsync(listing, props.swapDosingId, props.protocolId));
    navigate("../../../", { relative: 'path' });
  };

  const handleRemove = async(rowIndex: number) =>
  {
    if(!props.listings)
      return;

    appDispatch(deleteListingAsync(props.listings[rowIndex].listingId));
  };


  return (
    <MaUTable size='small' id='ListingsTable'>
      <TableHead>
        <TableRow>
          {headings.map((column, columnIndex) =>
          {
            let maxWidth: string | undefined = '5px';

            switch(column.accessor)
            {
            case 'listingName':
            case 'vendorName':
              maxWidth = undefined;
              break;
            case 'price':
              maxWidth = '80px';
              break;
            case 'priceWithFees':
              maxWidth = '120px';
              break;
            }

            return(
              <TableCell key={columnIndex} style={{ maxWidth }}>
                {(() =>
                {
                  const title = column.Header;
                  const accessor = column.accessor;

                  if(column.sortDirection !== undefined && column.setSortDirection)
                  {
                    return(
                      <HeadingSort
                        headingText={title}
                        sort={column.sortDirection}
                        handleToggleSort={handleToggleSort(
                          accessor === 'price' ? 'discountedPrice' : accessor,
                          column.sortDirection,
                          column.setSortDirection,
                          column.sortFn)} />);
                  }
                  else
                  {
                    return <b>{title}</b>;
                  }
                })()}
              </TableCell>);
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {values.map((row, rowIndex) =>
        {
          return (
            <TableRow key={rowIndex}>
              {['addToProtocol']
                .concat(props.swapDosingId ? ['swapIntoProtocol'] : [])
                .concat(props.listingName ? ['listingName'] : [])
                .concat(['vendorName', 'price', 'priceWithFees', 'link'])
                .concat(userListings ? ['remove'] : [])
                .map((key, index) =>
                {
                  let maxWidth: string | undefined = '5px';

                  switch(key)
                  {
                  case 'listingName':
                  case 'vendorName':
                    maxWidth = undefined;
                    break;
                  case 'price':
                    maxWidth = '80px';
                    break;
                  case 'priceWithFees':
                    maxWidth = '120px';
                    break;
                  }

                  switch(key)
                  {
                  case 'addToProtocol':
                    return(
                      <TableCell key={index} id='AddToProtocol' style={{ maxWidth }}>
                        <TextTooltip lines={["Add to protocol"]}>
                          <AddIcon
                            onClick={() => handleAddToProtocol(row.listingId)}
                            style={{ cursor: 'pointer', color: 'green' }}/>
                        </TextTooltip>
                      </TableCell>);
                  case 'swapIntoProtocol':
                    return(
                      <TableCell key={index} style={{ maxWidth }}>
                        <TextTooltip lines={["Swap into protocol"]}>
                          <ShoppingCartCheckoutIcon
                            onClick={() => handleSwapIntoProtocol(row)}
                            style={{ cursor: 'pointer', color: 'gray' }}/>
                        </TextTooltip>
                      </TableCell>);
                  case 'listingName':
                    return(
                      <TableCell key={index} style={{ maxWidth }}>
                        <Link
                          variant='body2'
                          underline='hover'
                          target='_blank'
                          rel='noreferrer'
                          style={{ cursor: 'pointer' }}
                          onClick={() => props.swapDosingId ?
                            handleSwapIntoProtocol(row) :
                            handleAddToProtocol(row.listingId)} >
                          {row[key]}
                        </Link>
                      </TableCell>);
                  case 'vendorName':
                    return(
                      <TableCell key={index} style={{ maxWidth }}>
                        <Stack direction='row'>
                          <Link
                            variant='body2'
                            underline='hover'
                            target='_blank'
                            rel='noreferrer'
                            style={{ cursor: 'pointer' }}
                            onClick={() => props.swapDosingId ?
                              handleSwapIntoProtocol(row) :
                              handleAddToProtocol(row.listingId)} >
                            {row[key]}
                          </Link>
                          {row.vendorInfo ?
                            <>
                              <Box width={8}/>
                              <TextTooltip lines={[row.vendorInfo]}>
                                <InfoIcon fontSize='small' sx={{ pt: -5, color: 'lightGray' }}/>
                              </TextTooltip>
                            </> :
                            <></>}
                        </Stack>
                      </TableCell>);
                  case 'price':
                    return(
                      <TableCell key={index} style={{ whiteSpace: 'nowrap', maxWidth }}>
                        <ListedPrice listingId={row.listingId}/>
                      </TableCell>);
                  case 'priceWithFees':
                    return(
                      <TableCell key={index} style={{ whiteSpace: 'nowrap', maxWidth }}>
                        <PriceWithTaxPlusDelivery listingId={row.listingId}/>
                      </TableCell>);
                  case 'link':
                    return(
                      <TableCell key={index} style={{ maxWidth }}>
                        <Box sx={{ height: 19 }}>
                          <LinkOpt url={row.url}/>
                        </Box>
                      </TableCell>);
                  case 'remove':
                    return(
                      (user.username && row.userId === user.userId) ?
                        <TableCell key={index} style={{ maxWidth }}>
                          <Box id='Remove' sx={{ height: 19 }}>
                            <TextTooltip lines={["Delete listing"]}>
                              <RemoveCircleIcon
                                fontSize='small'
                                onClick={() => handleRemove(rowIndex)}
                                style={{ cursor: 'pointer', color: 'red' }}/>
                            </TextTooltip>
                          </Box>
                        </TableCell> :
                        (userListings ? <TableCell key={index} style={{ maxWidth }}/> : <></>));
                  }
                })}
            </TableRow>);
        })}
        {props.bundles ?
          (user.username ?
            <LinkRow
              linkId={'Add'}
              rowKey={"addNew"}
              colSpan={headings.length}
              text={"Add a new bundle for your region"}
              handleClick={() => navigate(NEW, { state: { bundleProductId: storedProductId } })}/> :
            <LinkRow
              rowKey={"addNew"}
              colSpan={headings.length}
              text={"Log in to add a new bundle"}
              handleClick={() => navigate(LOGIN)}/>) :
          (user.username ?
            <LinkRow
              linkId={'Add'}
              rowKey={"addNew"}
              colSpan={headings.length}
              text={"Add a new listing for your region"}
              handleClick={() => navigate(NEW)}/> :
            <LinkRow
              rowKey={"addNew"}
              colSpan={headings.length}
              text={"Log in to add a new listing"}
              handleClick={() => navigate(LOGIN)}/>)}
      </TableBody>
    </MaUTable>);
};

export default ListingsTable;
